import { RotateCcw } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { IndexPage } from '../../../../components/index-page/IndexPage';
import type { ColumnDef, IndexQueryState } from '../../../../components/index-page/types';
import type { MenuItem } from '../../../../components/Menu';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { EvidenceStorageLocationModal } from '../components/EvidenceStorageLocationModal';
import { evidenceStorageLocationsApi } from '../evidenceApi';
import type { EvidenceStorageLocationListItem } from '../evidenceTypes';

/**
 * The evidence rooms, vaults and cabinets. Its own catalog, not Logistics' storage facilities —
 * a different grant over the same shape. Deleting one is password-confirmed and refused while
 * evidence is stored in it.
 */
export function EvidenceStorageLocationsIndexPage() {
  const navigate = useNavigate();
  const toast = useToast();

  const [editing, setEditing] = useState<EvidenceStorageLocationListItem | null>(null);
  const [creating, setCreating] = useState(false);
  const [confirming, setConfirming] = useState<{ kind: 'deactivate' | 'activate'; location: EvidenceStorageLocationListItem } | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  const fetchLocations = useCallback(
    (query: IndexQueryState, signal: AbortSignal) =>
      evidenceStorageLocationsApi.list(
        {
          search: query.search,
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort ?? undefined,
          status: query.facets.status?.join(','),
        },
        signal,
      ),
    [],
  );

  const rowActions = useCallback(
    (location: EvidenceStorageLocationListItem): MenuItem[] => [
      {
        key: `edit-${location.id}`,
        label: 'Edit',
        permission: 'evidence-storage-locations.update',
        onClick: () => setEditing(location),
      },
      {
        key: `restore-${location.id}`,
        label: 'Restore',
        icon: <RotateCcw className="h-4 w-4" aria-hidden="true" />,
        permission: 'evidence-storage-locations.delete',
        disabledReason: location.isActive ? 'This location is not deleted.' : null,
        onClick: () => setConfirming({ kind: 'activate', location }),
      },
    ],
    [],
  );

  async function runConfirmed(reauthentication?: string) {
    if (!confirming) return;

    setRunning(true);
    setError(null);

    try {
      if (confirming.kind === 'deactivate') await evidenceStorageLocationsApi.deactivate(confirming.location.id, reauthentication ?? '');
      else await evidenceStorageLocationsApi.activate(confirming.location.id);

      toast.success(`${confirming.location.name} was ${confirming.kind === 'deactivate' ? 'deleted' : 'restored'}.`);
      setConfirming(null);
      refresh();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0));
    } finally {
      setRunning(false);
    }
  }

  return (
    <>
      <IndexPage<EvidenceStorageLocationListItem>
        module="evidence-storage-locations"
        breadcrumb={['Evidence Management', { label: 'Settings', to: '/evidence/settings' }, 'Storage Locations']}
        columns={COLUMNS}
        facets={[{ key: 'status', label: 'Status' }]}
        auditEntityTypes={['EvidenceStorageLocation']}
        rowKey={(location) => location.id}
        fetch={fetchLocations}
        reloadKey={reloadKey}
        searchPlaceholder="Search name, code or description"
        emptyMessage="No storage locations yet. Add the rooms and vaults evidence is kept in."
        onCreate={() => setCreating(true)}
        createPermission="evidence-storage-locations.create"
        onRowOpen={(location) => navigate(`/evidence/settings/storage-locations/${location.id}`)}
        openPermission="evidence-storage-locations.read"
        rowActions={rowActions}
        onDelete={(location) => setConfirming({ kind: 'deactivate', location })}
        deletePermission="evidence-storage-locations.delete"
        deleteLabel={() => 'Delete'}
        deleteDisabledReason={(location) => (location.isActive ? null : 'This location is already deleted.')}
        otherInfo={(location) => location}
      />

      {creating || editing !== null ? (
        <EvidenceStorageLocationModal
          editing={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            toast.success(editing ? `${editing.name} was saved.` : 'The storage location was added.');
            setCreating(false);
            setEditing(null);
            refresh();
          }}
        />
      ) : null}

      {confirming ? (
        <ConfirmDialog
          open
          onClose={() => {
            setConfirming(null);
            setError(null);
          }}
          onConfirm={(reauthentication) => void runConfirmed(reauthentication)}
          title={confirming.kind === 'deactivate' ? 'Delete storage location' : 'Restore storage location'}
          danger={confirming.kind === 'deactivate'}
          requireConfirm={confirming.kind === 'deactivate'}
          requireCredential={confirming.kind === 'deactivate'}
          loading={running}
          id={`evidence-storage-location-${confirming.kind}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              <p>
                {confirming.kind === 'deactivate'
                  ? `"${confirming.location.name}" stops being offered as a place to keep evidence. It is refused while evidence is stored in it.`
                  : `"${confirming.location.name}" is offered again as a place to keep evidence.`}
              </p>
              <p>
                It keeps its code, {confirming.location.code}, for good — every custody line that names
                it still quotes it. Nothing is erased.
              </p>
              {error ? (
                <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                  {error.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </>
  );
}

const COLUMNS: ColumnDef<EvidenceStorageLocationListItem>[] = [
  { key: 'name', header: 'Name', width: 260, flex: true, sortable: true, render: (location) => <TextCell value={location.name} /> },
  { key: 'code', header: 'Code', width: 140, sortable: true, summary: true, render: (location) => <TextCell value={location.code} mono /> },
  { key: 'description', header: 'Description', width: 320, summary: true, render: (location) => <TextCell value={location.description} /> },
  {
    key: 'status',
    header: 'Status',
    width: 110,
    status: true,
    render: (location) =>
      location.isActive ? <StatusText tone="active">Active</StatusText> : <StatusText tone="inactive">Deleted</StatusText>,
  },
];
